import Phaser from 'phaser';
import { exposeGame } from './game/debug';
import { guardCancelledTouches } from './game/input/touchCancel';
import { computeGameSize } from './game/layout';
import { BootScene } from './game/scenes/BootScene';
import { HudScene } from './game/scenes/HudScene';
import { LevelScene } from './game/scenes/LevelScene';
import { MenuScene } from './game/scenes/MenuScene';
import { GarageScene } from './game/scenes/GarageScene';
import { LevelCardScene } from './game/scenes/LevelCardScene';
import { ResultsScene } from './game/scenes/ResultsScene';
import { SettingsScene } from './game/scenes/SettingsScene';
import { TrophyScene } from './game/scenes/TrophyScene';
import { RevealScene } from './game/scenes/RevealScene';
import { BuilderScene } from './game/scenes/BuilderScene';
import { MyLawnsScene } from './game/scenes/MyLawnsScene';
import { saves } from './game/records';
import { initCrashReports } from './platform/crashReports';
import { exitApp, initNativeShell, isNative, onAppShown, onBackButton } from './platform/native';
import { syncDailyReminders } from './game/reminders';
import { registerOfflineSupport } from './platform/pwa';
import { initStorage } from './platform/storage';
import { takeLinkedLawn } from './game/scenes/sharedLawn';
import { initFullGame } from './game/fullGame';
import { initOnline } from './game/online';
import { WipeScene, wipeTo } from './game/scenes/WipeScene';
import { TitleScene } from './game/scenes/TitleScene';
import { UnlockScene } from './game/scenes/UnlockScene';
import { ProgressScene } from './game/scenes/ProgressScene';
import { OnlineScene } from './game/scenes/OnlineScene';
import { BoardsScene } from './game/scenes/BoardsScene';
import { NameScene } from './game/scenes/NameScene';
import { FriendsScene } from './game/scenes/FriendsScene';
import { FriendScene } from './game/scenes/FriendScene';
import { ChallengeListScene } from './game/scenes/ChallengeListScene';
import { NicknameScene } from './game/scenes/NicknameScene';
import { InviteScene } from './game/scenes/InviteScene';
import { initPush } from './game/push';
import { InboxScene } from './game/scenes/InboxScene';
import { startHub } from './game/hub';
import { ChallengeScene } from './game/scenes/ChallengeScene';
import { VersusScene } from './game/scenes/VersusScene';
import { RaceScene } from './game/scenes/RaceScene';
import { RaceLevelScene } from './game/scenes/RaceLevelScene';
import { DuelLevelScene } from './game/scenes/DuelLevelScene';
import { CommunityScene } from './game/scenes/CommunityScene';
import { BlockedScene } from './game/scenes/BlockedScene';
import { listenForOpenedLinks, takeLinkedChallenge } from './game/links';
import { linkExit } from './game/linkExit';

const parent = document.getElementById('game');
if (!parent) throw new Error('#game container missing');

/** Size of the safe-area-padded container the canvas must fit in. */
function measure() {
  return computeGameSize(parent!.clientWidth, parent!.clientHeight);
}

// Native storage is read before anything touches the save.
await Promise.all([initStorage(), initNativeShell()]);
// A shared lawn's link (?lawn=CODE) or a challenge's (?challenge=ID): the menu opens it.
takeLinkedLawn();
takeLinkedChallenge();
// The full game: the store's word on the one-time purchase (in the background).
initFullGame();
// Online play: sends runs played offline (in the background; nothing when it is off).
initOnline();
// Before the game exists, so boot errors are caught (waits at most 2 s for the download);
// honors a saved opt-out from the start.
await initCrashReports(saves().save.settings.crashReports);
// The Daily reminders (native, opt-in): a week ahead from now, again whenever the app comes back.
syncDailyReminders();
onAppShown(() => syncDailyReminders());

const initial = measure();
// iOS home swipes cancel the touch: never let that press a button.
guardCancelledTouches();

const game = new Phaser.Game({
  type: Phaser.AUTO,
  parent,
  backgroundColor: '#1d2b1f',
  pixelArt: true,
  // More than one finger (Phaser follows one by default): a tap on an item button while the
  // steering thumb is still down must not be dropped (owner, 2026-11-04).
  input: { activePointers: 3 },
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    width: initial.width,
    height: initial.height,
  },
  scene: [
    BootScene,
    MenuScene,
    LevelCardScene,
    LevelScene,
    HudScene,
    ResultsScene,
    GarageScene,
    TrophyScene,
    RevealScene,
    MyLawnsScene,
    BuilderScene,
    SettingsScene,
    UnlockScene,
    ProgressScene,
    OnlineScene,
    BoardsScene,
    NameScene,
    FriendsScene,
    FriendScene,
    ChallengeListScene,
    NicknameScene,
    InviteScene,
    InboxScene,
    ChallengeScene,
    VersusScene,
    RaceScene,
    RaceLevelScene,
    DuelLevelScene,
    CommunityScene,
    BlockedScene,
    WipeScene,
    TitleScene,
  ],
});

exposeGame(game);
// Natively, a playlawnmower.com link that opens the app (universal links).
listenForOpenedLinks(game);
startHub(game);
// Friend alerts (docs/FRIENDS.md, step 4): a tapped one opens its invite or the INBOX.
initPush();
if (!isNative()) registerOfflineSupport(() => game.scene.isActive('Level'));

// Android back: pause (or leave) a lawn, back out of other screens, exit from the main menu.
/**
 * Android's back button. The topmost screen that answers it itself does: a lawn pauses, a race or
 * a match asks before leaving, the lobby leaves its room, a friend's invite card closes. Else the
 * menu leaves the app, the builder goes back to its lawns, and any other screen goes to the menu,
 * left from the screen itself rather than an overlay on it (`linkExit`: the HUD or the results
 * left the play drawn over the menu, lm-q7e).
 */
onBackButton(() => {
  const active = game.scene.getScenes(true);
  const own = [...active]
    .reverse()
    .find(
      (s): s is Phaser.Scene & { onBack(): void } =>
        typeof (s as { onBack?: unknown }).onBack === 'function',
    );
  if (own) {
    own.onBack();
  } else if (game.scene.isActive('Menu')) {
    exitApp();
  } else if (game.scene.isActive('Builder')) {
    // The builder backs out to its list of lawns.
    wipeTo(game.scene.getScene('Builder'), 'MyLawns', {});
  } else {
    const exit = linkExit(active.map((s) => s.scene.key));
    if (exit && exit !== 'wait') wipeTo(game.scene.getScene(exit.from), 'Menu');
  }
});

// Keep the internal height matched to the space the game has: rotation, browser chrome, and the
// safe area settling after launch (a container resize with no window resize), checked again when
// the app comes back to the foreground.
const fit = () => {
  const next = measure();
  if (next.height !== game.scale.gameSize.height) {
    game.scale.setGameSize(next.width, next.height);
  }
  game.scale.refresh();
};
window.addEventListener('resize', fit);
new ResizeObserver(fit).observe(parent);
onAppShown(() => requestAnimationFrame(fit));
